import { Component, Input, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { TranslateModule } from '@ngx-translate/core';
import {
  AlternativeAvailabilityService,
  AvailabilityService,
  IAlternativeAvailabilityResult,
  IAlternativeHotelResult,
  ILaterStayOption,
  ISearchParams,
} from 'core-lib';
import { DisplayPricePipe } from '../../pipes/displayPrice';

/** One small day box: a day offset from the searched dates and its availability. */
export interface IDayCell extends IAlternativeAvailabilityResult {
  /** Days from the searched arrival (negative = earlier, 0 = the searched day itself). */
  offset: number;
}

/** How many day boxes are visible at once; the arrows page through the days by this many. */
const DAY_CELL_COUNT = 3;

/**
 * Renders up to three suggestion boxes below the booking-engine's "no availability"
 * message: availability in our other hotels, a nearby date (previous/next day), and
 * the same stay a configured time later (1 week, 2 weeks or 1 month). The other-hotels box has no on/off switch and the
 * frontend cannot see the backend-only cross-tenant configuration, so it always asks.
 * The other-hotels box with no availability at all (or a failed load) hides itself once
 * loaded, and the remaining boxes grow to fill the row. The date boxes (nearby days, next
 * week) stay visible whenever they load, because their arrows let the user look further away. Each box loads its data independently
 * (so a slow box never blocks the others).
 */
@Component({
  selector: 'app-alternative-availability',
  standalone: true,
  imports: [ButtonModule, SkeletonModule, TranslateModule, DisplayPricePipe, DatePipe],
  templateUrl: './alternative-availability.component.html',
  styleUrl: './alternative-availability.component.scss',
  host: { '[class.has-boxes]': 'showOtherHotels || showDayShift || showLaterStay' },
})
export class AlternativeAvailabilityComponent implements OnInit {

  /** Original (failed) search parameters; the boxes shift dates / swap hotels off this. */
  @Input({ required: true }) searchParams!: ISearchParams;

  showOtherHotels = false;
  showDayShift = false;
  showLaterStay = false;

  otherHotelsLoading = false;
  dayShiftLoading = false;
  laterStayLoading = false;

  otherHotels: IAlternativeHotelResult[] = [];
  /** The visible day boxes: previous day, the searched day, next day (before any paging). */
  dayCells: IDayCell[] = [];
  dayCellsLoading = false;
  /** Offset of the first visible day box; the arrows move it by a whole page. */
  private dayWindowStart = -1;

  laterStay: IAlternativeAvailabilityResult | null = null;
  laterStayStepLoading = false;
  /** Configured offset of the later-stay box (weeks or a month); null when the box is off. */
  laterStayOption: ILaterStayOption | null = null;
  /** Current shift of the later-stay row in option units; the arrows step it by one. */
  private laterStaySteps = 1;

  readonly maxShiftDays = 30;

  constructor(
    private altService: AlternativeAvailabilityService,
    private avService: AvailabilityService,
  ) { }

  ngOnInit(): void {
    this.showOtherHotels = true;
    this.otherHotelsLoading = true;
    this.altService.getOtherHotels(this.searchParams).subscribe({
      next: (hotels) => {
        this.otherHotels = hotels;
        this.showOtherHotels = hotels.some(h => h.hasAvailability);
        this.otherHotelsLoading = false;
      },
      error: () => {
        this.showOtherHotels = false;
        this.otherHotelsLoading = false;
      },
    });

    if (this.altService.isDayShiftEnabled()) {
      this.showDayShift = true;
      this.dayShiftLoading = true;
      this.altService.getDayShift(this.searchParams).subscribe({
        next: (result) => {
          this.dayCells = [
            { ...result.previous, offset: -1 },
            this.searchedDayCell(),
            { ...result.next, offset: 1 },
          ];
          this.dayShiftLoading = false;
        },
        error: () => {
          this.showDayShift = false;
          this.dayShiftLoading = false;
        },
      });
    }

    this.laterStayOption = this.altService.getLaterStayOption();
    if (this.laterStayOption) {
      this.showLaterStay = true;
      this.laterStayLoading = true;
      this.altService.getLaterStay(this.searchParams, this.laterStayOption).subscribe({
        next: (result) => {
          this.laterStay = result;
          this.laterStayLoading = false;
        },
        error: () => {
          this.showLaterStay = false;
          this.laterStayLoading = false;
        },
      });
    }
  }

  /**
   * Same-tenant hotel: re-run the search in-app for that hotel code.
   * Cross-tenant hotel: build a deeplink (without the booking code, which is
   * tenant-specific) to the other booking engine and navigate the browser there.
   */
  selectOtherHotel(hotel: IAlternativeHotelResult): void {
    if (hotel.hotelCode) {
      this.avService.selectHotelAndMakeSearch(hotel.hotelCode);
    } else if (this.isLinkable(hotel)) {
      const url = this.avService.buildDeeplinkUrl(
        hotel.bookingEngineUrl as string,
        { ...this.searchParams, hotelCode: String(hotel.hotelId) },
        true,
      );
      this.navigateExternal(url);
    }
  }

  /** Whether selecting the hotel leads anywhere: a same-tenant hotel, or a cross-tenant one with a configured booking engine URL. */
  isLinkable(hotel: IAlternativeHotelResult): boolean {
    return !!hotel.hotelCode || (!!hotel.bookingEngineUrl && hotel.hotelId != null);
  }

  /**
   * Browser navigation to another tenant's booking engine. Extracted so the unit
   * tests can assert the deeplink was built without triggering a real page reload
   * in the Karma test runner.
   */
  protected navigateExternal(url: string): void {
    window.location.href = url;
  }

  /** Whether the day boxes can page a whole page earlier (-1) or later (1) within the maximum shift. */
  canPageDays(direction: -1 | 1): boolean {
    if (this.dayCellsLoading) {
      return false;
    }
    const start = this.dayWindowStart + direction * DAY_CELL_COUNT;
    return start >= -this.maxShiftDays && start + DAY_CELL_COUNT - 1 <= this.maxShiftDays;
  }

  /** Loads the previous or next three days; the searched day itself is known to have no availability. */
  pageDays(direction: -1 | 1): void {
    if (!this.canPageDays(direction)) {
      return;
    }
    const start = this.dayWindowStart + direction * DAY_CELL_COUNT;
    const offsets = Array.from({ length: DAY_CELL_COUNT }, (_, i) => start + i);
    this.dayCellsLoading = true;
    forkJoin(offsets.map(offset => offset === 0
      ? of(this.searchedDayCell())
      : this.altService.getShifted(this.searchParams, offset).pipe(map((result): IDayCell => ({ ...result, offset }))),
    )).subscribe({
      next: (cells) => {
        this.dayCells = cells;
        this.dayWindowStart = start;
        this.dayCellsLoading = false;
      },
      error: () => {
        this.dayCellsLoading = false;
      },
    });
  }

  /** Re-run the original search for the stay shifted to the given day box. Unavailable days are not selectable. */
  selectDay(cell: IDayCell): void {
    if (!cell.hasAvailability) {
      return;
    }
    this.avService.makeSearch({
      ...this.searchParams,
      arrival: cell.arrival,
      departure: cell.departure,
    });
  }

  /** The searched stay as a day box: it was searched because it has no availability. */
  private searchedDayCell(): IDayCell {
    return {
      offset: 0,
      arrival: this.avService.toApiDate(this.searchParams.arrival as string),
      departure: this.avService.toApiDate(this.searchParams.departure as string),
      hasAvailability: false,
      minPrice: null,
      roomsAvailable: 0,
    };
  }

  /** Whether the later-stay row can step one option unit earlier (-1) or later (1) without crossing the original dates or the maximum. */
  canStepLaterStay(direction: -1 | 1): boolean {
    if (this.laterStayStepLoading || !this.laterStayOption) {
      return false;
    }
    const target = this.laterStaySteps + direction;
    return target >= 1 && target <= this.laterStayOption.maxSteps;
  }

  /** Moves the later-stay row one option unit earlier or later and reloads its availability. */
  stepLaterStay(direction: -1 | 1): void {
    if (!this.laterStayOption || !this.canStepLaterStay(direction)) {
      return;
    }
    const steps = this.laterStaySteps + direction;
    this.laterStayStepLoading = true;
    this.altService.getLaterStay(this.searchParams, this.laterStayOption, steps).subscribe({
      next: (result) => {
        this.laterStaySteps = steps;
        this.laterStay = result;
        this.laterStayStepLoading = false;
      },
      error: () => {
        this.laterStayStepLoading = false;
      },
    });
  }

  /** Re-run the original search shifted by the configured later-stay offset. No-op until resolved. */
  selectLaterStay(): void {
    if (!this.laterStay) {
      return;
    }
    this.avService.makeSearch({
      ...this.searchParams,
      arrival: this.laterStay.arrival,
      departure: this.laterStay.departure,
    });
  }
}
